import type { AdminQueryPort } from "../../contracts/admin-query-port";
import type { AdminQueryError } from "../../contracts/admin-query-errors";
import type {
  AdminQueryEnvelope,
  AdminRecognitionAuditDetail,
  AdminRecognitionAuditPage,
  AdminRecognitionAuditQuery,
  AdminRecognitionRawAccessGrant,
  AdminRecognitionRawAccessRequest,
} from "../../contracts/admin-read-models";

export type RecognitionAuditRawAccessResult =
  | {
      ok: true;
      envelope: AdminQueryEnvelope<AdminRecognitionRawAccessGrant>;
      reason: string;
    }
  | {
      ok: false;
      error: AdminQueryError;
    };

function createUnsupportedMethodError(methodName: string): AdminQueryError {
  return {
    code: "ADMIN_QUERY_INTERNAL_ERROR",
    message: `${methodName} is not available on the current admin query port.`,
  };
}

function createValidationError(message: string): AdminQueryError {
  return {
    code: "ADMIN_QUERY_INVALID_FILTER",
    message,
  };
}

export function createRecognitionAuditUseCases(adminQueryPort: AdminQueryPort) {
  return {
    async loadList(
      query: AdminRecognitionAuditQuery,
    ): Promise<AdminQueryEnvelope<AdminRecognitionAuditPage>> {
      if (!adminQueryPort.searchRecognitionAudits) {
        throw createUnsupportedMethodError("searchRecognitionAudits");
      }

      return adminQueryPort.searchRecognitionAudits(query);
    },
    async loadDetail(
      recognitionRunId: string,
    ): Promise<AdminQueryEnvelope<AdminRecognitionAuditDetail>> {
      if (!adminQueryPort.getRecognitionAuditDetail) {
        throw createUnsupportedMethodError("getRecognitionAuditDetail");
      }

      return adminQueryPort.getRecognitionAuditDetail({ recognitionRunId });
    },
    async requestRawAccess(
      input: AdminRecognitionRawAccessRequest,
    ): Promise<RecognitionAuditRawAccessResult> {
      const reason = input.reason.trim();

      if (reason.length < 10) {
        return {
          ok: false,
          error: createValidationError("Raw access reason must be at least 10 characters."),
        };
      }

      if (reason.length > 500) {
        return {
          ok: false,
          error: createValidationError("Raw access reason must be 500 characters or less."),
        };
      }

      if (!adminQueryPort.issueRecognitionRawAccess) {
        return {
          ok: false,
          error: createUnsupportedMethodError("issueRecognitionRawAccess"),
        };
      }

      return {
        ok: true,
        envelope: await adminQueryPort.issueRecognitionRawAccess({
          ...input,
          reason,
        }),
        reason,
      };
    },
  };
}
